import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    styled,
    useTheme,
    IconButton,
    Tooltip,
    Paper,
    Chip,
    Typography,
    CircularProgress,
    Avatar,
    Collapse,
    Card,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Button,
} from "@mui/material";
import { Refresh, AttachFile, Phone, Email, KeyboardArrowDown, KeyboardArrowUp, Close } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import FlexBetween from "components/flexbox/FlexBetween";
import AppPaginationBar from "components/AppPaginationBar";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { Fragment, useState } from "react";

const TABLE_CELL_PADDING = "12px 16px";
const FARMER_COLUMN_WIDTH = 170;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: TABLE_CELL_PADDING,
    color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    whiteSpace: "nowrap",
    "&.header": {
        fontWeight: 600,
        fontSize: "0.875rem",
        backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.background.paper
            : theme.palette.grey[200],
        backgroundImage: "none",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:hover": {
        backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.grey[800]
            : theme.palette.grey[100],
    },
    "& .sticky-body-cell": {
        backgroundColor: theme.palette.background.paper,
        backgroundImage: "none",
    },
    "&:hover .sticky-body-cell": {
        backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.grey[800]
            : theme.palette.grey[100],
    },
    "&.Mui-selected .sticky-body-cell, &.Mui-selected:hover .sticky-body-cell": {
        backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.grey[700]
            : theme.palette.grey[200],
    },
}));

// ─── CHANGE 1: Destructure canViewImage from props ─────────────────────────────
const FieldVisitTable = ({ fieldVisits, searchQuery, loading, onRefresh, pagination = null, onPageChange, onPageSizeChange, pageSizeOptions = [10, 25, 50, 100], canViewImage = true }) => {
// ─────────────────────────────────────────────────────────────────────────────
    const theme = useTheme();
    const [openRow, setOpenRow] = useState(null);
    const [openImageDialog, setOpenImageDialog] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const SR_NO_COLUMN_WIDTH = 58;
    const VISIT_DATE_COLUMN_WIDTH = 120;
    const VISIT_DATE_STICKY_LEFT = SR_NO_COLUMN_WIDTH;
    const FARMER_NAME_STICKY_LEFT = VISIT_DATE_STICKY_LEFT + VISIT_DATE_COLUMN_WIDTH;
    const handleRowClick = (rowId) => {
        setOpenRow((prev) => (prev === rowId ? null : rowId));
    };

    const filteredFieldVisits = fieldVisits.filter(visit => {
        if (searchQuery) {
            const searchLower = searchQuery.toLowerCase();
            
            const productSubcategoryMatch = visit.product_subcategory?.some(product => 
                product.name?.toLowerCase().includes(searchLower) ||
                product.sku?.toLowerCase().includes(searchLower)
            );
            
            const cropMatch = visit.crop?.name?.toLowerCase().includes(searchLower) ||
                (typeof visit.crop === 'string' && visit.crop.toLowerCase().includes(searchLower));
            
            const matchesSearch = (
                visit.farmer_name?.toLowerCase().includes(searchLower) ||
                visit.village?.toLowerCase().includes(searchLower) ||
                visit.district?.toLowerCase().includes(searchLower) ||
                visit.executive_name?.toLowerCase().includes(searchLower) ||
                visit.farmer_email?.toLowerCase().includes(searchLower) ||
                visit.farmer_contact?.includes(searchLower) ||
                visit.product_vertical?.name?.toLowerCase().includes(searchLower) ||
                cropMatch ||
                productSubcategoryMatch
            );
            
            if (!matchesSearch) return false;
        }
        
        return true;
    });

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const getInitials = (name) => {
        if (!name) return 'N/A';
        const nameParts = name.split(' ');
        if (nameParts.length >= 2) {
            return `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
        }
        return name.charAt(0).toUpperCase();
    };

    const handleFileView = (fileUrl) => {
        if (fileUrl) {
            const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${API_ENDPOINTS.BASE_URL || ''}${fileUrl}`;
            setSelectedImageUrl(fullUrl);
            setOpenImageDialog(true);
        }
    };

    const handleCloseImageDialog = () => {
        setOpenImageDialog(false);
        setSelectedImageUrl('');
    };

    const renderShowingText = () => {
        if (pagination?.total_items != null) {
            if (pagination.total_items > 0) {
                const start = (pagination.current_page - 1) * pagination.page_size + 1;
                const end = Math.min(pagination.current_page * pagination.page_size, pagination.total_items);
                return `Showing ${start} - ${end} of ${pagination.total_items}`;
            }
            return "Total: 0 visits";
        }

        const total = filteredFieldVisits.length;
        return `Total: ${total} ${total === 1 ? "visit" : "visits"}`;
    };

    return (
        <Box sx={{ width: "100%", p: 2 }}>
            <FlexBetween mb={2} px={1}>
                <Typography variant="h6" fontWeight={600}>
                    Field Visit Records
                </Typography>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    {pagination && onPageSizeChange && (
                      <FormControl size="small" sx={{ minWidth: 110 }}>
                        <InputLabel>Rows</InputLabel>
                        <Select
                          value={pagination.page_size}
                          label="Rows"
                          onChange={onPageSizeChange}
                        >
                          {pageSizeOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <Button
                        variant="contained"
                        onClick={onRefresh}
                        disabled={loading}
                        startIcon={<Refresh />}
                        sx={{ 
                            minWidth: "auto",
                            px: 2,
                            borderRadius: 2,
                            height: 40,
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Refresh
                    </Button>
                </Box>
            </FlexBetween>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>Loading field visits...</Typography>
                </Box>
            ) : filteredFieldVisits.length > 0 ? (
                <TableContainer component={Paper} sx={{ position: 'relative', height: 750, maxHeight:700, overflowY: 'auto', overflowX: 'auto', scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#555 #1e1e1e"
                : "#c1c1c1 #f1f1f1",
            "&::-webkit-scrollbar": {
              height: 8,
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              background:
                theme.palette.mode === "dark"
                  ? "#1e1e1e"
                  : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "dark"
                  ? "#555"
                  : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background:
                theme.palette.mode === "dark"
                  ? "#777"
                  : "#a8a8a8",
            }, }}>
                    <Table stickyHeader>
                        <TableHead sx={{ position: 'sticky', top: 0, zIndex: 50 }}>
                            <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}>
                                <StyledTableCell
                                    className="header"
                                    sx={{
                                        padding: "12px 4px",
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 60,
                                        width: SR_NO_COLUMN_WIDTH,
                                        minWidth: SR_NO_COLUMN_WIDTH,
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? theme.palette.background.paper
                                            : theme.palette.grey[200],
                                    }}
                                >
                                    Sr. No.
                                </StyledTableCell>
                                <StyledTableCell
                                    className="header"
                                    sx={{
                                        padding: "12px 6px",
                                        position: 'sticky',
                                        left: VISIT_DATE_STICKY_LEFT,
                                        zIndex: 59,
                                        width: VISIT_DATE_COLUMN_WIDTH,
                                        minWidth: VISIT_DATE_COLUMN_WIDTH,
                                        textAlign: 'center',
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? theme.palette.background.paper
                                            : theme.palette.grey[200],
                                    }}
                                >
                                    Visit Date
                                </StyledTableCell>
                                <StyledTableCell
                                    className="header"
                                    sx={{
                                        position: 'sticky',
                                        left: FARMER_NAME_STICKY_LEFT,
                                        zIndex: 58,
                                        width: FARMER_COLUMN_WIDTH,
                                        minWidth: FARMER_COLUMN_WIDTH,
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? theme.palette.background.paper
                                            : theme.palette.grey[200],
                                    }}
                                >
                                    Farmer Details
                                </StyledTableCell>
                                <StyledTableCell className="header">Location</StyledTableCell>
                                <StyledTableCell className="header">Contact</StyledTableCell>
                                <StyledTableCell className="header">Product Vertical</StyledTableCell>
                                <StyledTableCell className="header">Crop</StyledTableCell>
                                <StyledTableCell className="header">Products</StyledTableCell>
                                <StyledTableCell className="header">Farm Area</StyledTableCell>
                                <StyledTableCell className="header">Plantation Date</StyledTableCell>
                                <StyledTableCell className="header">Observations</StyledTableCell>
                                <StyledTableCell className="header">Recommendations</StyledTableCell>
                                <StyledTableCell className="header">Executive</StyledTableCell>
                                <StyledTableCell className="header">Upload File</StyledTableCell>
                                <StyledTableCell className="header">Added By</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredFieldVisits.map((visit, index) => {
                                const isOpen = openRow === visit.id;
                                return (
                                    <Fragment key={visit.id}>
                                        <StyledTableRow onClick={() => handleRowClick(visit.id)} sx={{ cursor: "pointer" }}>
                                    <StyledTableCell
                                        className="sticky-body-cell"
                                        sx={{
                                            padding: "12px 4px",
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 34,
                                            width: SR_NO_COLUMN_WIDTH,
                                            minWidth: SR_NO_COLUMN_WIDTH,
                                            textAlign: 'center',
                                            backgroundColor: theme.palette.background.paper,
                                            backgroundImage: 'none',
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, textAlign: 'center' }}>
                                        {pagination && pagination.current_page && pagination.page_size
                                            ? (pagination.current_page - 1) * pagination.page_size + (index + 1)
                                            : index + 1}
                                    </Typography>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell
                                        className="sticky-body-cell"
                                        sx={{
                                            padding: "12px 6px",
                                            position: 'sticky',
                                            left: VISIT_DATE_STICKY_LEFT,
                                            zIndex: 33,
                                            width: VISIT_DATE_COLUMN_WIDTH,
                                            minWidth: VISIT_DATE_COLUMN_WIDTH,
                                            textAlign: 'center',
                                            backgroundColor: theme.palette.background.paper,
                                            backgroundImage: 'none',
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} width="100%">
                                            <IconButton
                                                size="small"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setOpenRow(isOpen ? null : visit.id);
                                                }}
                                                onDoubleClick={(event) => event.stopPropagation()}
                                            >
                                                {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                            </IconButton>
                                            <Typography variant="body2" fontWeight={500}>
                                                {formatDate(visit.visit_date)}
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell
                                        className="sticky-body-cell"
                                        sx={{
                                            position: 'sticky',
                                            left: FARMER_NAME_STICKY_LEFT,
                                            zIndex: 32,
                                            width: FARMER_COLUMN_WIDTH,
                                            minWidth: FARMER_COLUMN_WIDTH,
                                            backgroundColor: theme.palette.background.paper,
                                            backgroundImage: 'none',
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                            <Avatar
                                                sx={{ 
                                                    width: 36, 
                                                    height: 36,
                                                    bgcolor: theme.palette.primary.main,
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {getInitials(visit.farmer_name)}
                                            </Avatar>
                                            <Box>
                                                <Tooltip title={visit.farmer_name || 'N/A'} arrow>
                                                    <Typography 
                                                        variant="subtitle2" 
                                                        fontWeight={600}
                                                        sx={{ 
                                                            maxWidth: 150, 
                                                            overflow: 'hidden', 
                                                            textOverflow: 'ellipsis', 
                                                            whiteSpace: 'nowrap',
                                                            cursor: 'help'
                                                        }}
                                                    >
                                                        {visit.farmer_name || 'N/A'}
                                                    </Typography>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Tooltip 
                                            title={`${visit.village || ''}${visit.tehsil ? `, ${visit.tehsil}` : ''}\n${visit.district || ''}, ${visit.state || ''}`} 
                                            arrow
                                        >
                                            <Box>
                                                <Typography 
                                                    variant="body2"
                                                    sx={{ 
                                                        maxWidth: 150, 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'help'
                                                    }}
                                                >
                                                    {visit.village}{visit.tehsil ? `, ${visit.tehsil}` : ''}
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    color="text.secondary"
                                                    sx={{ 
                                                        maxWidth: 150, 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'help',
                                                        display: 'block'
                                                    }}
                                                >
                                                    {visit.district}, {visit.state}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Box>
                                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                                <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                <Typography variant="body2">
                                                    {visit.farmer_contact || 'N/A'}
                                                </Typography>
                                            </Box>
                                            {visit.farmer_email && (
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                    <Tooltip title={visit.farmer_email} arrow>
                                                        <Typography 
                                                            variant="caption" 
                                                            color="text.secondary"
                                                            sx={{ 
                                                                maxWidth: 150, 
                                                                overflow: 'hidden', 
                                                                textOverflow: 'ellipsis', 
                                                                whiteSpace: 'nowrap',
                                                                cursor: 'help'
                                                            }}
                                                        >
                                                            {visit.farmer_email}
                                                        </Typography>
                                                    </Tooltip>
                                                </Box>
                                            )}
                                        </Box>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Chip 
                                            label={visit.company_vertical?.name || 'N/A'} 
                                            size="small" 
                                            color="success"
                                            variant="outlined"
                                        />
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        {visit.crop ? (
                                            <Chip 
                                                label={visit.crop.name || visit.crop} 
                                                size="small" 
                                                color="secondary"
                                                variant="outlined"
                                            />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                N/A
                                            </Typography>
                                        )}
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        {visit.product_subcategory && visit.product_subcategory.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                {visit.product_subcategory.map((product, index) => (
                                                    <Box key={product.id || index}>
                                                        <Tooltip title={product.name || '-'} arrow>
                                                            <Typography 
                                                                variant="body2" 
                                                                fontWeight={500}
                                                                sx={{ 
                                                                    maxWidth: 200, 
                                                                    overflow: 'hidden', 
                                                                    textOverflow: 'ellipsis', 
                                                                    whiteSpace: 'nowrap',
                                                                    cursor: 'help'
                                                                }}
                                                            >
                                                                {product.name}
                                                            </Typography>
                                                        </Tooltip>
                                                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                                            {product.sku && (
                                                                <Chip 
                                                                    label={`SKU: ${product.sku}`} 
                                                                    size="small" 
                                                                    variant="outlined"
                                                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                No products
                                            </Typography>
                                        )}
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Typography variant="body2">
                                            {visit.farm_area_value 
                                                ? `${visit.farm_area_value} ${visit.farm_area_unit || ''}`
                                                : 'N/A'
                                            }
                                        </Typography>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Typography variant="body2">
                                            {formatDate(visit.plantation_date)}
                                        </Typography>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Tooltip title={visit.observations || '-'} arrow>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    maxWidth: 200, 
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {visit.observations || '-'}
                                            </Typography>
                                        </Tooltip>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Tooltip title={visit.recommendations || '-'} arrow>
                                            <Typography 
                                                variant="body2"
                                                sx={{ 
                                                    maxWidth: 200, 
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {visit.recommendations || '-'}
                                            </Typography>
                                        </Tooltip>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Tooltip title={visit.executive_name || 'N/A'} arrow>
                                            <Typography 
                                                variant="body2" 
                                                fontWeight={500}
                                                sx={{ 
                                                    maxWidth: 120, 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {visit.executive_name || 'N/A'}
                                            </Typography>
                                        </Tooltip>
                                    </StyledTableCell>
                                    
                                    {/* ─── CHANGE 2: View Image button — disabled when no view_image permission ─ */}
                                    <StyledTableCell>
                                        {visit.upload_file ? (
                                            <Tooltip title={canViewImage ? "View Image" : "No permission to view image"}>
                                                <span>
                                                    <IconButton 
                                                        size="small" 
                                                        color="primary"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            if (canViewImage) {
                                                                handleFileView(visit.upload_file);
                                                            }
                                                        }}
                                                        disabled={!canViewImage}
                                                    >
                                                        <AttachFile fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                No file
                                            </Typography>
                                        )}
                                    </StyledTableCell>
                                    {/* ─────────────────────────────────────────────────────────────────────── */}
                                    
                                    <StyledTableCell>
                                        <Box>
                                            <Typography variant="body2" fontWeight={500}>
                                                {visit.added_by?.first_name} {visit.added_by?.last_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {visit.added_by?.email}
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                </StyledTableRow>

                                <TableRow>
                                    <TableCell colSpan={15} sx={{ p: 0, border: 0 }}>
                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                            <Card
                                                sx={(theme) => ({
                                                    m: 1,
                                                    p: 2,
                                                    backgroundColor: theme.palette.mode === 'dark' 
                                                        ? alpha(theme.palette.background.paper, 0.8)
                                                        : alpha(theme.palette.primary.main, 0.02),
                                                    borderRadius: 2,
                                                })}
                                            >
                                                <Typography 
                                                    variant="h6" 
                                                    fontWeight={600} 
                                                    sx={{ mb: 2, textDecoration: 'underline' }}
                                                >
                                                    Field Visit Details - #{visit.id}
                                                </Typography>

                                                {visit.product_subcategory && visit.product_subcategory.length > 0 ? (
                                                    <>
                                                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                                                            Products
                                                        </Typography>
                                                        <Table size="small" sx={{ mb: 2, width: 'auto' }}>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell sx={{ fontWeight: 600, pr: 2 }}>Product Name</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600, pl: 21 }}>SKU</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {visit.product_subcategory.map((product, index) => (
                                                                    <TableRow
                                                                        key={product.id || index}
                                                                        sx={{
                                                                            '&:hover': {
                                                                                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                                                            },
                                                                        }}
                                                                    >
                                                                        <TableCell sx={{ pr: 8 }}>
                                                                            <Typography variant="body2" fontWeight={500}>
                                                                                {product.name}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell sx={{ pl: 21 }}>{product.sku || '-'}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                        No products listed
                                                    </Typography>
                                                )}

                                                <Divider sx={{ my: 2 }} />
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'flex-start',
                                                        flexWrap: 'wrap',
                                                        columnGap: 8,
                                                        rowGap: 3,
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Visit Date:</strong> {formatDate(visit.visit_date)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Plantation Date:</strong> {formatDate(visit.plantation_date)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Farm Area:</strong> {visit.farm_area_value 
                                                                ? `${visit.farm_area_value} ${visit.farm_area_unit || ''}`
                                                                : 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Product Vertical:</strong> {visit.product_vertical?.name || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Crop:</strong> {visit.crop?.name || visit.crop || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Farmer Name:</strong> {visit.farmer_name || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Contact:</strong> {visit.farmer_contact || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Email:</strong> {visit.farmer_email || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Location:</strong> {visit.village}{visit.tehsil ? `, ${visit.tehsil}` : ''}, {visit.district}, {visit.state}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Executive:</strong> {visit.executive_name || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ maxWidth: '300px' }}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Observations:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                                                            {visit.observations || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Recommendations:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                                                            {visit.recommendations || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Feedback:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {visit.farmer_feedback || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Total Products:</strong> {visit.product_subcategory?.length || 0}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Added By:</strong> {visit.added_by?.first_name} {visit.added_by?.last_name}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Card>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box 
                    sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8
                    }}
                >
                    <Typography 
                        variant="h6" 
                        color="text.secondary"
                        align="center"
                    >
                        {searchQuery 
                            ? "No field visits found matching your search"
                            : "No field visit records available"
                        }
                    </Typography>
                </Box>
            )}

            <Dialog
                open={openImageDialog}
                onClose={handleCloseImageDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: theme.palette.background.paper,
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" component="div">
                        Image view
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseImageDialog}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    {selectedImageUrl && (
                        <img 
                            src={selectedImageUrl} 
                            alt="Preview" 
                            style={{ 
                                maxWidth: '100%', 
                                maxHeight: '70vh', 
                                objectFit: 'contain',
                                borderRadius: '8px'
                            }} 
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseImageDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

         <AppPaginationBar
    pagination={
        searchQuery
            ? filteredFieldVisits.length > 0
                ? {
                    ...pagination,
                    total_items: filteredFieldVisits.length,
                    total_pages: Math.ceil(filteredFieldVisits.length / (pagination?.page_size || 10)),
                    current_page: 1,
                  }
                : null
            : pagination
    }
    onPageChange={onPageChange}
    itemLabel="field visits"
/>
        </Box>
    );
};

export default FieldVisitTable;